<template>
  <div class="tutorial-overlay" v-if="visible">
    <div class="tutorial-card">
      <button type="button" class="btn btn-ghost btn-sm tutorial-skip" @click="skip">Skip</button>

      <div class="tutorial-icon"><i class="fas" :class="currentStep.icon"></i></div>
      <h3 class="tutorial-title">{{ currentStep.title }}</h3>
      <p class="tutorial-text">{{ currentStep.text }}</p>

      <div class="tutorial-dots">
        <span
          v-for="(step, i) in steps"
          :key="i"
          class="tutorial-dot"
          :class="{ 'tutorial-dot--active': i === stepIndex }"
        ></span>
      </div>

      <div class="tutorial-actions">
        <button v-if="stepIndex > 0" type="button" class="btn btn-secondary" @click="back">Back</button>
        <button type="button" class="btn btn-primary tutorial-next" @click="next">{{ isLastStep ? "Let's go" : 'Next' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TutorialOverlay',
  props: {
    targetLanguage: {
      type: String,
      default: 'your target language'
    }
  },
  data() {
    return {
      stepIndex: 0
    };
  },
  computed: {
    // Store-owned so App.vue can host this above the router.
    visible() {
      return this.$store.state.settings.showTutorial;
    },
    steps() {
      return [
        {
          icon: 'fa-hand-sparkles',
          title: 'Welcome to EZ Shadow',
          text: `Practice speaking ${this.targetLanguage} by shadowing real YouTube videos - watch a sentence, then say it back.`
        },
        {
          icon: 'fa-headphones',
          title: 'Listen, then speak',
          text: 'Tap Play to hear a sentence. When it pauses, repeat it out loud into your mic.'
        },
        {
          icon: 'fa-star',
          title: 'Instant feedback',
          text: "We'll score your pronunciation right away. Retry to improve, or move on to the next sentence."
        },
        {
          icon: 'fa-cog',
          title: 'Make it yours',
          text: 'Pick any YouTube video and change your language anytime from Settings.'
        }
      ];
    },
    currentStep() {
      return this.steps[this.stepIndex];
    },
    isLastStep() {
      return this.stepIndex === this.steps.length - 1;
    }
  },
  watch: {
    visible(isVisible) {
      if (isVisible) this.stepIndex = 0;
    }
  },
  methods: {
    next() {
      if (this.isLastStep) {
        this.complete('completed');
      } else {
        this.stepIndex++;
      }
    },
    back() {
      if (this.stepIndex > 0) {
        this.stepIndex--;
      }
    },
    skip() {
      this.complete('skipped');
    },
    complete(outcome) {
      // value = step reached, so a skip at step 1 reads differently from step 3.
      if (window.$analytics) {
        window.$analytics.trackEvent('tutorial_finished', {
          category: 'Onboarding',
          label: outcome || 'completed',
          value: this.stepIndex
        });
      }
      // The router reads this to decide on the landing page.
      localStorage.setItem('tutorialCompleted', 'true');
      this.$store.dispatch('settings/closeTutorial');
      this.$emit('tutorial-completed');
    }
  }
}
</script>

<style scoped>
.tutorial-overlay {
  position: fixed;
  inset: 0;
  background: var(--scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.tutorial-card {
  position: relative;
  width: 100%;
  max-width: 340px;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sheet);
  box-shadow: var(--shadow-pop);
  padding: 32px 24px 24px;
  text-align: center;
}

.tutorial-skip {
  position: absolute;
  top: 10px;
  right: 10px;
}

.tutorial-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: var(--r-pill);
  background: var(--accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: var(--accent);
}

.tutorial-title {
  margin: 0 0 8px;
  font-size: 19px;
  font-weight: 700;
  color: var(--text);
}

.tutorial-text {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--muted);
}

.tutorial-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 20px;
}

.tutorial-dot {
  width: 6px;
  height: 6px;
  border-radius: var(--r-pill);
  background: var(--field-hover);
  transition: background 0.2s, width 0.2s;
}

.tutorial-dot--active {
  background: var(--accent);
  width: 18px;
}

.tutorial-actions {
  display: flex;
  gap: 8px;
}

.tutorial-next { flex: 1; }
</style>
